// ============================================================================
// Canonical Layer Housing record route contract
// ============================================================================
//
// `{view: 'layerbatches', paths: ['/layer/batches/*', '/layer/housings/*']}` is
// a TWO-PATH row, and LayerBatchesRouter picks between three shapes on the RAW
// pathname alone:
//
//   /layer/housings/<id>  -> layer.housings.detail  (LayerHousingPage)
//   /layer/housings/      -> layer.housings.detail  (empty id, "not found")
//   /layer/batches/<id>   -> layer.batches.detail   (LayerBatchPage, LEGACY)
//   anything else         -> layer.batches.index    (LayerBatchesHub, released)
//
// Only the FIRST of those is released, and only with exactly one decoded-safe
// segment. The trailing-slash base is deliberately excluded: the router really
// does mount LayerHousingPage there — `housingId` is the empty string, so the
// page paints its own "Housing not found." — but that is not the direct-entry
// route any reviewer audited, and the generic resolver classifies it as this
// same surface. An unfenced RELEASED verdict would suppress recovery, so
// admitting it would strand the frame instead of merely rendering a narrower
// page.
//
// This module knows nothing about the ledger, the renderer, or any data owner.
// Its only job is to keep the reviewed shape from drifting as wide as the
// historical router, which accepts every `/layer/housings/` descendant. The
// released boundary keeps wider raw shapes in explicit recovery.
//
// RESERVED SEGMENTS: there are NONE beneath `/layer/housings/`.
// `src/lib/routes.ts` registers no `/layer/housings` path at all — the view's
// canonical `VIEW_TO_PATH.layerbatches` is `/layer/batches`, and the layer
// domain appears in neither ALIASES_EXACT nor ALIASES_PREFIX. LAYER_ROUTES
// declares no sibling namespace inside the housings splat (unlike
// `/cattle/batches/forecast` or TasksRouter's `todo`), and the router's own
// branch is a bare `startsWith` with the page slicing the remainder — no value
// is special-cased. That absence is DERIVED in the release proof, not asserted
// here: a future `/layer/housings/archive` row would have to add a reserved set
// to this module AND appear in that proof; nothing else would notice it.
// ============================================================================

import {isCanonicalCommentAnchorHash} from './commentAnchorHash.ts';

export const LAYER_HOUSINGS_DETAIL_PATHNAME_PREFIX = '/layer/housings/';

type LocationLike = Readonly<{pathname?: unknown; search?: unknown; hash?: unknown}>;

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/**
 * `/layer/housings/<id>` with exactly one decoded-safe segment.
 *
 * `/layer/housings` does not start with the prefix and `/layer/housings/` ends
 * at it, so this predicate claims neither. The raw segment is checked BEFORE
 * the decoder so a literal separator, NUL, or line break never reaches it, then
 * the value is decoded once and re-checked so an encoded separator, dot
 * segment, NUL, or line break cannot widen the record release.
 */
export function isCanonicalLayerHousingsDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(LAYER_HOUSINGS_DETAIL_PATHNAME_PREFIX)) {
    return false;
  }
  const rawSegment = pathname.slice(LAYER_HOUSINGS_DETAIL_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The record page READS `location.hash`: it scrolls a deep-linked
 * `#comment-<id>` mention from RecordCollaborationSection into view. A
 * hash-carrying load is therefore a materially different render than the
 * audited one, so both search and hash stay outside the release and receive
 * explicit route recovery. This is the same line the released Layer Dailys
 * record draws, for the same reason.
 *
 * A bare string is accepted only when it is plainly a pathname, so a caller or
 * test seam cannot smuggle an unsupported query or fragment into the target.
 */
export function isCanonicalLayerHousingsDetailLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalLayerHousingsDetailPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;

  const {pathname, search, hash} = location as LocationLike;
  return (
    (search === undefined || search === '') &&
    (hash === undefined || hash === '' || isCanonicalCommentAnchorHash(hash)) &&
    isCanonicalLayerHousingsDetailPathname(pathname)
  );
}
